/**
 * TenantsTableCard — the rich Ant-Design tenants table used by both AdminPage
 * and SuperAdminPage. Owns its own column-visibility, column-width, search,
 * filter, and sort state so the parent doesn't have to.
 *
 * Action surface is parent-driven:
 *   - `onCreate`        → renders the "New Tenant" header button
 *   - `onEdit`          → renders an Edit action per row (super-admin path)
 *   - `onToggleStatus`  → status column becomes a live toggle; falls back to
 *                         a read-only badge when not supplied
 *   - `onDelete`        → renders a Delete action per row
 *
 * This component intentionally does NOT fetch — `tenants` and `hierarchyTree`
 * are pushed in by the parent so SuperAdminPage and AdminPage keep their own
 * data lifecycles.
 */

import { useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Table, Input, Button, Space, Radio, Checkbox, Dropdown } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { SearchOutlined, SettingOutlined, DeleteOutlined, EditOutlined, FormOutlined } from '@ant-design/icons';
import type { TenantHierarchy, HierarchyTreeNode } from '@modules/group_admin/types';

// The phone breakpoint the admin tables already follow (styles/mobile.css).
const PHONE_QUERY = '(max-width: 768px)';
// Room for the row's icon buttons side by side (edit, rename, delete) on a phone.
const PHONE_ACTIONS_WIDTH = 120;

// ─── Hierarchical descendant / total-user roll-up ──────────────────────────
function calculateHierarchicalCounts(
  tree: HierarchyTreeNode | null,
): Map<string, { descendantCount: number; totalUsers: number }> {
  const counts = new Map<string, { descendantCount: number; totalUsers: number }>();
  if (!tree) return counts;

  function traverse(node: HierarchyTreeNode): { descendantCount: number; totalUsers: number } {
    let descendantCount = 0;
    let totalUsers = node.user_count ?? 0;

    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        const childCounts = traverse(child);
        descendantCount += 1 + childCounts.descendantCount;
        totalUsers += childCounts.totalUsers;
      }
    }
    counts.set(node.tenant_id, { descendantCount, totalUsers });
    return { descendantCount, totalUsers };
  }

  traverse(tree);
  return counts;
}

// ─── Resizable <th> ─────────────────────────────────────────────────────────
function ResizableTitle(props: any) {
  const { onResize, width, ...restProps } = props;
  const thRef = useRef<HTMLTableCellElement>(null);

  if (!onResize) return <th {...restProps} />;

  return (
    <th
      ref={thRef}
      {...restProps}
      style={{ ...restProps.style, position: 'relative', userSelect: 'none', width: width || 'auto' }}
      onMouseDown={(e) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        if (e.clientX < rect.right - 10) return;

        e.preventDefault();
        const startX = e.clientX;
        const startWidth = width || thRef.current?.offsetWidth || 100;

        const handleMouseMove = (moveEvent: MouseEvent) => {
          const newWidth = startWidth + (moveEvent.clientX - startX);
          if (newWidth >= 140) onResize(newWidth);
        };
        const handleMouseUp = () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          document.body.style.cursor = 'default';
          document.body.style.userSelect = 'auto';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
      }}
    >
      {restProps.children}
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '10px',
          cursor: 'col-resize',
          background: 'transparent',
          zIndex: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      />
    </th>
  );
}

// ─── Numeric range/exact/gte/lte filter dropdown — shared by 3 columns ─────
function NumericFilterDropdown({
  setSelectedKeys, selectedKeys, confirm, clearFilters,
}: FilterDropdownProps) {
  const { t } = useLanguage();
  const filterValue = selectedKeys[0] ? String(selectedKeys[0]).split(':') : ['exact', '', ''];
  const mode = filterValue[0] || 'exact';
  const value1 = filterValue[1] || '';
  const value2 = filterValue[2] || '';

  const updateFilter = (newMode: string, newValue1: string, newValue2: string) => {
    setSelectedKeys([`${newMode}:${newValue1}:${newValue2}`]);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') confirm();
  };

  return (
    <div style={{ padding: 8, width: 200 }}>
      <Radio.Group
        value={mode}
        onChange={(e) => updateFilter(e.target.value, value1, value2)}
        style={{ marginBottom: 8, display: 'block' }}
      >
        <Radio value="exact">{t('admin.filterExact')}</Radio>
        <Radio value="gte">{t('admin.filterGreaterOrEqual')}</Radio>
        <Radio value="lte">{t('admin.filterLessOrEqual')}</Radio>
        <Radio value="range">{t('admin.filterRange')}</Radio>
      </Radio.Group>
      {mode === 'exact' && (
        <Input dir="auto" placeholder={t('admin.filterNumber')} value={value1} onChange={(e) => updateFilter(mode, e.target.value, value2)} onKeyPress={handleKeyPress} style={{ marginBottom: 8 }} />
      )}
      {mode === 'gte' && (
        <Input dir="auto" placeholder={t('admin.filterMinValue')} value={value1} onChange={(e) => updateFilter(mode, e.target.value, value2)} onKeyPress={handleKeyPress} style={{ marginBottom: 8 }} />
      )}
      {mode === 'lte' && (
        <Input dir="auto" placeholder={t('admin.filterMaxValue')} value={value1} onChange={(e) => updateFilter(mode, e.target.value, value2)} onKeyPress={handleKeyPress} style={{ marginBottom: 8 }} />
      )}
      {mode === 'range' && (
        <>
          <Input dir="auto" placeholder={t('admin.filterMin')} value={value1} onChange={(e) => updateFilter(mode, e.target.value, value2)} onKeyPress={handleKeyPress} style={{ marginBottom: 8 }} />
          <Input dir="auto" placeholder={t('admin.filterMax')} value={value2} onChange={(e) => updateFilter(mode, value1, e.target.value)} onKeyPress={handleKeyPress} style={{ marginBottom: 8 }} />
        </>
      )}
      <Space>
        <Button onClick={() => confirm()} size="small" type="primary">{t('common.filter')}</Button>
        <Button onClick={() => { clearFilters?.(); confirm(); }} size="small">{t('common.reset')}</Button>
      </Space>
    </div>
  );
}

function applyNumericFilter(value: any, count: number): boolean {
  if (!value) return true;
  const parts = String(value).split(':');
  const mode = parts[0];
  const value1Str = parts[1] || '';
  const value2Str = parts[2] || '';

  if (mode === 'exact') {
    if (!value1Str) return true;
    const n = parseInt(value1Str, 10);
    if (isNaN(n)) return true;
    return count === n;
  }
  if (mode === 'gte') {
    if (!value1Str) return true;
    const n = parseInt(value1Str, 10);
    if (isNaN(n)) return true;
    return count >= n;
  }
  if (mode === 'lte') {
    if (!value1Str) return true;
    const n = parseInt(value1Str, 10);
    if (isNaN(n)) return true;
    return count <= n;
  }
  if (mode === 'range') {
    if (!value1Str && !value2Str) return true;
    const min = value1Str ? parseInt(value1Str, 10) : Number.MIN_SAFE_INTEGER;
    const max = value2Str ? parseInt(value2Str, 10) : Number.MAX_SAFE_INTEGER;
    if (isNaN(min) && isNaN(max)) return true;
    const effMin = isNaN(min) ? Number.MIN_SAFE_INTEGER : min;
    const effMax = isNaN(max) ? Number.MAX_SAFE_INTEGER : max;
    return count >= effMin && count <= effMax;
  }
  return true;
}

// ─── Text contains/exact filter dropdown — shared by 3 columns ────────────
function TextFilterDropdown({
  setSelectedKeys, selectedKeys, confirm, clearFilters, placeholder,
}: FilterDropdownProps & { placeholder: string }) {
  const { t } = useLanguage();
  return (
    <div style={{ padding: 12, width: 280 }}>
      <Radio.Group
        value={selectedKeys[1] || 'contains'}
        onChange={(e) => {
          const newKeys = [...selectedKeys];
          newKeys[1] = e.target.value;
          setSelectedKeys(newKeys);
        }}
        style={{ marginBottom: 8, display: 'block' }}
      >
        <Radio value="contains">{t('admin.filterContains')}</Radio>
        <Radio value="exact">{t('admin.filterExactMatch')}</Radio>
      </Radio.Group>
      <Input dir="auto"
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={(e) => {
          setSelectedKeys(e.target.value ? [e.target.value, selectedKeys[1] || 'contains'] : []);
          confirm({ closeDropdown: false });
        }}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button onClick={() => clearFilters?.()} size="small">{t('common.reset')}</Button>
      </Space>
    </div>
  );
}

function applyTextFilter(value: any, fieldValue: string): boolean {
  const searchValue = Array.isArray(value) ? value[0] : value;
  const matchType = Array.isArray(value) ? value[1] : 'contains';
  const searchTerm = (searchValue as string).toLowerCase();
  const fv = fieldValue.toLowerCase();
  return matchType === 'exact' ? fv === searchTerm : fv.includes(searchTerm);
}

// ─── Props ─────────────────────────────────────────────────────────────────
export interface TenantsTableCardProps {
  tenants: TenantHierarchy[];
  hierarchyTree: HierarchyTreeNode | null;
  /** Used to disable the status toggle on the current user's own tenant. */
  currentUserTenantId?: string;
  loading?: boolean;
  /** Show "New Tenant" button in the header. */
  canCreate?: boolean;
  /** Fired when "New Tenant" is clicked. */
  onCreate?: () => void;
  /** When provided, an Edit action is rendered per row. */
  onEdit?: (tenant: TenantHierarchy) => void;
  /** When provided, a Rename action is rendered per row (group admins and super admins). */
  onRename?: (tenant: TenantHierarchy) => void;
  /** When provided, the status column is a live toggle; otherwise a read-only badge. */
  onToggleStatus?: (tenantId: string) => void;
  /** When provided, a Delete action is rendered per row. */
  onDelete?: (tenantId: string, tenantName: string) => void;
  /**
   * Caller-supplied columns inserted between the built-in Status column and
   * the Actions column. Each gets its own Columns-dropdown checkbox derived
   * from `column.title` (string-only) so super-admins can hide noise.
   */
  extraColumns?: ColumnsType<TenantHierarchy>;
}

// ─── Component ─────────────────────────────────────────────────────────────
export default function TenantsTableCard({
  tenants,
  hierarchyTree,
  currentUserTenantId,
  loading,
  canCreate,
  onCreate,
  onEdit,
  onRename,
  onToggleStatus,
  onDelete,
  extraColumns,
}: TenantsTableCardProps) {
  const { t } = useLanguage();
  const phone = useMediaQuery(PHONE_QUERY);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'tenant_name',
    'tenant_code',
    'tenant_domain',
    'parent_tenant_id',
    'user_count',
    'sub_tenants',
    'all_users',
    'is_active',
  ]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number | undefined>>({
    tenant_name: 220,
    tenant_code: 170,
    tenant_domain: 240,
    parent_tenant_id: 220,
    user_count: 190,
    sub_tenants: 190,
    all_users: 180,
    is_company: 100,
    is_active: 170,
    actions: 180,
  });

  const allTenantsColumns: ColumnsType<TenantHierarchy> = [
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.tenantName')}</div>,
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      align: 'center' as const,
      width: columnWidths.tenant_name,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, tenant_name: newWidth })),
      }),
      sorter: (a, b) => a.tenant_name.localeCompare(b.tenant_name),
      filterDropdown: (props) => <TextFilterDropdown {...props} placeholder={t('admin.searchTenantName')} />,
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => applyTextFilter(value, record.tenant_name),
      render: (name) => <span>{name}</span>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.tenantsTableCode')}</div>,
      dataIndex: 'tenant_code',
      key: 'tenant_code',
      align: 'center' as const,
      width: columnWidths.tenant_code,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, tenant_code: newWidth })),
      }),
      sorter: (a, b) => a.tenant_code.localeCompare(b.tenant_code),
      filterDropdown: (props) => <TextFilterDropdown {...props} placeholder={t('admin.searchTenantCode')} />,
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => applyTextFilter(value, record.tenant_code),
      render: (code) => <code>{code}</code>,
    },
    {
      // Per-tenant pre-auth login URL. Effective value = custom
      // tenant_domain if set, else the backend-computed subdomain_url
      // (https://<tenant_subdomain>.corpbrain.com, scheme stripped for
      // table density). Reads existing tenant fields — no new columns
      // invented. Both fields are editable from the White-Label Editor's
      // "Custom login URL" field. Em-dash only when the tenant has
      // neither a custom domain nor a subdomain configured.
      title: <div style={{ textAlign: 'center' }}>{t('admin.loginUrl')}</div>,
      key: 'tenant_domain',
      align: 'center' as const,
      width: columnWidths.tenant_domain,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, tenant_domain: newWidth })),
      }),
      sorter: (a, b) => {
        const av = a.tenant_domain || (a.subdomain_url || '').replace(/^https?:\/\//, '');
        const bv = b.tenant_domain || (b.subdomain_url || '').replace(/^https?:\/\//, '');
        return av.localeCompare(bv);
      },
      filterDropdown: (props) => <TextFilterDropdown {...props} placeholder={t('admin.searchDomain')} />,
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => {
        const v = record.tenant_domain || (record.subdomain_url || '').replace(/^https?:\/\//, '');
        return applyTextFilter(value, v);
      },
      render: (_: unknown, record: TenantHierarchy) => {
        // Real, openable login URL built from the current window origin
        // + the path-based branded login URL /<tenant_code>/login.
        // Runtime-evaluated so it always reflects the actual deployment
        // URL (localhost, Cloud Run, custom domain in prod) — no
        // hardcoding.
        if (!record.tenant_code) {
          return <span style={{ color: 'var(--text-muted)' }}>—</span>;
        }
        const slug = encodeURIComponent(record.tenant_code.toLowerCase());
        const href = `${window.location.origin}/${slug}/login`;
        const display = `${window.location.host}/${record.tenant_code.toLowerCase()}/login`;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="tenant-url-cell-prod"
            title={t('admin.opensThisTenant')}
          >
            {display}
          </a>
        );
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.parentTenant')}</div>,
      dataIndex: 'parent_tenant_id',
      key: 'parent_tenant_id',
      align: 'center' as const,
      width: columnWidths.parent_tenant_id,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, parent_tenant_id: newWidth })),
      }),
      sorter: (a, b) => {
        const aParent = tenants.find(t => t.tenant_id === a.parent_tenant_id)?.tenant_name || '';
        const bParent = tenants.find(t => t.tenant_id === b.parent_tenant_id)?.tenant_name || '';
        return aParent.localeCompare(bParent);
      },
      filterDropdown: (props) => <TextFilterDropdown {...props} placeholder={t('admin.searchParentTenant')} />,
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => {
        const parentTenant = tenants.find(t => t.tenant_id === record.parent_tenant_id);
        return applyTextFilter(value, parentTenant ? parentTenant.tenant_name : '');
      },
      render: (parent_tenant_id) => {
        const parentTenant = tenants.find(t => t.tenant_id === parent_tenant_id);
        return parentTenant
          ? <span dir="auto">{parentTenant.tenant_name}</span>
          : <span style={{ color: 'var(--text-muted)' }}>{t('admin.rootTenant')}</span>;
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.directUsers')}</div>,
      dataIndex: 'user_count',
      key: 'user_count',
      align: 'center' as const,
      width: columnWidths.user_count,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, user_count: newWidth })),
      }),
      sorter: (a, b) => (a.user_count ?? 0) - (b.user_count ?? 0),
      filterDropdown: NumericFilterDropdown,
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => applyNumericFilter(value, record.user_count ?? 0),
      render: (count) => <span>{count ?? 0}</span>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.subTenants')}</div>,
      key: 'sub_tenants',
      align: 'center' as const,
      width: columnWidths.sub_tenants,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, sub_tenants: newWidth })),
      }),
      sorter: (a, b) => {
        const c = calculateHierarchicalCounts(hierarchyTree);
        return (c.get(a.tenant_id)?.descendantCount ?? 0) - (c.get(b.tenant_id)?.descendantCount ?? 0);
      },
      filterDropdown: NumericFilterDropdown,
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => {
        const c = calculateHierarchicalCounts(hierarchyTree);
        return applyNumericFilter(value, c.get(record.tenant_id)?.descendantCount ?? 0);
      },
      render: (_, record) => {
        const c = calculateHierarchicalCounts(hierarchyTree);
        return <span>{c.get(record.tenant_id)?.descendantCount ?? 0}</span>;
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.allUsers')}</div>,
      key: 'all_users',
      align: 'center' as const,
      width: columnWidths.all_users,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, all_users: newWidth })),
      }),
      sorter: (a, b) => {
        const c = calculateHierarchicalCounts(hierarchyTree);
        return (c.get(a.tenant_id)?.totalUsers ?? 0) - (c.get(b.tenant_id)?.totalUsers ?? 0);
      },
      filterDropdown: NumericFilterDropdown,
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => {
        const c = calculateHierarchicalCounts(hierarchyTree);
        return applyNumericFilter(value, c.get(record.tenant_id)?.totalUsers ?? 0);
      },
      render: (_, record) => {
        const c = calculateHierarchicalCounts(hierarchyTree);
        return <span>{c.get(record.tenant_id)?.totalUsers ?? 0}</span>;
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.company')}</div>,
      dataIndex: 'is_company',
      key: 'is_company',
      align: 'center' as const,
      width: columnWidths.is_company,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, is_company: newWidth })),
      }),
      filters: [
        { text: t('admin.company'), value: true },
        { text: t('admin.notCompany'), value: false },
      ],
      onFilter: (value, record) => !!record.is_company === value,
      render: (isCompany: boolean) =>
        isCompany ? (
          <span title={t('admin.companyLoginGateway')} style={{ fontSize: 16 }}>🏢</span>
        ) : null,
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.status')}</div>,
      dataIndex: 'tenant_status',
      key: 'is_active',
      align: 'center' as const,
      width: columnWidths.is_active,
      onHeaderCell: (column: any) => ({
        width: column.width,
        onResize: (newWidth: number) => setColumnWidths(prev => ({ ...prev, is_active: newWidth })),
      }),
      filters: [
        { text: t('admin.active'), value: 'active' },
        { text: t('admin.inactive'), value: 'inactive' },
      ],
      onFilter: (value, record) => record.tenant_status === value,
      render: (status, record) => {
        // Read-only badge when no toggle handler is supplied.
        if (!onToggleStatus) {
          return (
            <span className={`status-badge ${status === 'active' ? 'status-active' : 'status-suspended'}`}>
              {status === 'active' ? t('common.active') : t('common.inactive')}
            </span>
          );
        }
        const isSelf = !!currentUserTenantId && record.tenant_id === currentUserTenantId;
        return (
          <label className={`status-switch ${isSelf ? 'disabled' : ''}`}>
            <input
              type="checkbox"
              checked={status === 'active'}
              onChange={() => onToggleStatus(record.tenant_id)}
              disabled={isSelf}
            />
            <span className="switch-slider"></span>
            <span className="switch-label">{status === 'active' ? t('common.active') : t('common.inactive')}</span>
          </label>
        );
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('admin.actions')}</div>,
      key: 'actions',
      align: 'center' as const,
      width: phone ? PHONE_ACTIONS_WIDTH : columnWidths.actions,
      render: (_, record) => {
        const isSelf = !!currentUserTenantId && record.tenant_id === currentUserTenantId;
        return (
          <Space size={4}>
            {onEdit && (
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                title={t('admin.editTenantTitle')}
              />
            )}
            {onRename && (
              // Labelled on a wide screen (Segev 2026-10-31: a bare icon is easy to
              // miss); on a phone the icon alone, named for screen readers and on
              // hover, so Delete stays in view without scrolling sideways.
              <Button
                type="text"
                icon={<FormOutlined />}
                onClick={() => onRename(record)}
                title={phone ? t('groupAdmin.rename.action') : undefined}
                aria-label={phone ? t('groupAdmin.rename.action') : undefined}
                data-testid={`tenant-rename-${record.tenant_id}`}
              >
                {phone ? null : t('groupAdmin.rename.action')}
              </Button>
            )}
            {onDelete && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record.tenant_id, record.tenant_name)}
                title={isSelf ? t('admin.cannotDeleteOwnTenant') : t('admin.deleteTenant')}
                disabled={record.is_root_tenant || isSelf}
              />
            )}
          </Space>
        );
      },
    },
  ];

  // Column order: Tenant Name → Actions → (rest of builtins) → extras.
  // Actions sits next to the tenant name so admins can edit / delete
  // without having to scan across the whole row first.
  const allButActions = allTenantsColumns.slice(0, -1);
  const actionsCol = allTenantsColumns[allTenantsColumns.length - 1];
  const [nameCol, ...restCols] = allButActions;
  const extras = extraColumns ?? [];
  const fullColumns: ColumnsType<TenantHierarchy> = [nameCol, actionsCol, ...restCols, ...extras];
  const extraKeys = new Set(extras.map(c => c.key as string));

  const filteredTenantsColumns = fullColumns.filter(col => {
    if (col.key === 'actions') return true;
    if (extraKeys.has(col.key as string)) return true;
    return visibleColumns.includes(col.key as string);
  });

  // Column-visibility toggle row. Inline so the JSX matches AdminPage 1:1.
  const COLUMN_TOGGLES: Array<{ key: string; label: string }> = [
    { key: 'tenant_name', label: t('groupAdmin.tenants.tenantName') },
    { key: 'tenant_code', label: t('admin.tenantsTableCode') },
    { key: 'parent_tenant_id', label: t('admin.parentTenant') },
    { key: 'user_count', label: t('admin.directUsers') },
    { key: 'sub_tenants', label: t('admin.subTenants') },
    { key: 'all_users', label: t('admin.allUsers') },
    { key: 'is_active', label: t('admin.status') },
  ];

  const totalUsers = tenants.reduce((sum, t) => sum + (t.user_count || 0), 0);
  const activeCount = tenants.filter(t => t.tenant_status === 'active').length;

  return (
    <div className="card">
      <div className="card-header">
        <h3>{t('admin.managedTenants')}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flex: 1, justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
            <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{tenants.length}</span> tenants
          </div>
          <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
            <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{activeCount}</span> active
          </div>
          <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
            <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{totalUsers}</span> total users
          </div>
        </div>
        {canCreate && onCreate && (
          <button className="btn-primary btn-sm" onClick={onCreate}>
            <svg viewBox="0 0 24 24" width="14" height="14" style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2 }}>
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            {t('admin.newTenant')}
          </button>
        )}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '0',
        borderBottom: '1px solid var(--claude-border)',
        background: 'var(--claude-bg-tertiary)',
        flexShrink: 0,
      }}>
        <Dropdown
          trigger={['click']}
          dropdownRender={() => (
            <div style={{
              background: 'var(--claude-bg-secondary)',
              border: '1px solid var(--claude-border)',
              borderRadius: 'var(--radius)',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            }}>
              <div style={{ padding: '4px 0' }}>
                {COLUMN_TOGGLES.map(({ key, label }) => (
                  <div
                    key={key}
                    style={{
                      padding: '4px 8px',
                      cursor: 'pointer',
                      borderRadius: 'var(--radius)',
                      transition: 'background 0.2s',
                      background: 'var(--claude-bg-secondary)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--claude-bg-tertiary)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--claude-bg-secondary)'; }}
                  >
                    <Checkbox
                      checked={visibleColumns.includes(key)}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) setVisibleColumns([...visibleColumns, key]);
                        else setVisibleColumns(visibleColumns.filter(col => col !== key));
                      }}
                    >
                      {label}
                    </Checkbox>
                  </div>
                ))}
              </div>
            </div>
          )}
        >
          <button
            className="admin-columns-btn"
            style={{
              padding: 'var(--space-2) var(--space-3)',
              background: 'transparent',
              border: 'none',
              color: 'var(--claude-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--claude-bg-secondary)';
              e.currentTarget.style.color = 'var(--claude-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--claude-text-secondary)';
            }}
          >
            <SettingOutlined style={{ fontSize: '14px' }} />
            {t('admin.columns')}
          </button>
        </Dropdown>
      </div>

      <div className="table-wrapper">
        <Table<TenantHierarchy>
          columns={filteredTenantsColumns}
          components={{ header: { cell: ResizableTitle } }}
          dataSource={tenants}
          rowKey="tenant_id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
          locale={{ emptyText: t('common.noTenantsFound') }}
        />
      </div>
    </div>
  );
}
